import * as React from 'react'
import { useEffect, useState } from 'react'
import { useTranslate } from 'react-admin'
import ContractSigningDialog from '../ContractSigningDialog/ContractSigningDialog'
import getOrgContractStatus from '../../Apis/contract/getOrgContractStatus'
import getOrgContract from '../../Apis/contract/getOrgContract'
import acceptOrgContract from '../../Apis/contract/acceptOrgContract'
import localStorageIds from '../../utils/constants/localStorageIds'

const readSelectedOrgId = () => {
  try {
    return JSON.parse(localStorage.getItem(localStorageIds.SELECTED_ORG))?.id || ''
  } catch {
    return ''
  }
}

const serverMessage = (error) => {
  const data = error?.response?.data
  return data?.details || data?.message || ''
}

/**
 * Blocks the dashboard while the selected org owes a signature a super admin asked for, and takes
 * the acceptance. Mounted from MyLayout like ConsentPopup; scoped to the selected org so an owner
 * of several orgs is only blocked inside the one being asked.
 */
const ContractSigningPrompt = () => {
  const translate = useTranslate()

  const [selectedOrgId, setSelectedOrgId] = useState(readSelectedOrgId)
  const [contract, setContract] = useState(null)
  const [open, setOpen] = useState(false)
  const [accepting, setAccepting] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  // SELECTED_ORG is written through helpers that dispatch a synthetic `storage` event.
  useEffect(() => {
    const onStorage = () => setSelectedOrgId(readSelectedOrgId())
    window.addEventListener('storage', onStorage)

    return () => window.removeEventListener('storage', onStorage)
  }, [])

  useEffect(() => {
    let cancelled = false

    setOpen(false)
    setContract(null)
    setErrorMessage('')

    if (!selectedOrgId) return undefined

    const decide = async () => {
      let status
      try {
        status = await getOrgContractStatus(selectedOrgId)
      } catch {
        return
      }
      if (!status?.signatureRequired || cancelled) return

      setOpen(true)

      try {
        const current = await getOrgContract(selectedOrgId, { skipErrorNotification: true })
        if (!cancelled) setContract(current)
      } catch {
        if (!cancelled) setOpen(false)
      }
    }

    decide()

    return () => {
      cancelled = true
    }
  }, [selectedOrgId])

  // A refused acceptance may mean the document was re-issued while on screen: show the new one.
  const refreshAfterDocumentChange = async (documentHash) => {
    let current
    try {
      current = await getOrgContract(selectedOrgId, { skipErrorNotification: true })
    } catch {
      return false
    }

    if (!current || current.documentHash === documentHash) return false

    setContract(current)
    setErrorMessage(translate('ra.kickoffContract.versionChanged'))

    return true
  }

  const handleAccept = async (documentHash) => {
    if (accepting) return

    setAccepting(true)
    setErrorMessage('')
    try {
      await acceptOrgContract(selectedOrgId, documentHash)
      setOpen(false)
      setContract(null)
    } catch (error) {
      if (await refreshAfterDocumentChange(documentHash)) return

      // Same document, so not a re-issue. If the signature is no longer owed (signed in another
      // tab), let the owner through rather than trapping them behind a dialog with no dismiss.
      try {
        const status = await getOrgContractStatus(selectedOrgId)
        if (!status?.signatureRequired) {
          setOpen(false)
          setContract(null)
          return
        }
      } catch {
        // Could not establish that the signature is no longer needed; leave the prompt up.
      }

      setErrorMessage(serverMessage(error) || translate('ra.errorMessages.errorMessages'))
    } finally {
      setAccepting(false)
    }
  }

  if (!open) return null

  return (
    <ContractSigningDialog
      open={open}
      contract={contract}
      accepting={accepting}
      errorMessage={errorMessage}
      onAccept={handleAccept}
      subtitle={translate('ra.existingOrgContract.subtitle')}
    />
  )
}

export default ContractSigningPrompt
